import React from 'react';
import { Link } from 'react-router-dom';
import './LegalNotices.css';

const LegalNotices: React.FC = () => {
  const legalPages = [
    {
      id: 'terms',
      title: 'Terms & Conditions',
      description: 'Our terms of service, user agreements, and service conditions',
      url: '/terms',
      icon: '⚖️',
      color: '#667eea',
      lastUpdated: '09/01/2025',
      category: 'Service Terms'
    },
    {
      id: 'privacy',
      title: 'Privacy Policy',
      description: 'How we collect, use, and protect your personal information',
      url: '/privacy',
      icon: '🔒',
      color: '#4b9ca2',
      lastUpdated: '09/01/2025',
      category: 'Data Protection'
    },
    {
      id: 'dpa',
      title: 'Data Processing Addendum',
      description: 'GDPR, CCPA, and data processing agreements for business customers',
      url: '/dpa',
      icon: '📋',
      color: '#4b9ca2',
      lastUpdated: '09/01/2025',
      category: 'Data Protection'
    },
    {
      id: 'subprocessors',
      title: 'Subprocessors',
      description: 'Third-party service providers we use to deliver our services',
      url: '/subprocessors',
      icon: '🏗️',
      color: '#667eea',
      lastUpdated: '09/01/2025',
      category: 'Compliance'
    },
    {
      id: 'dmca',
      title: 'DMCA Notice',
      description: 'Copyright policy and procedures for reporting infringement',
      url: '/dmca',
      icon: '©️',
      color: '#c95549',
      lastUpdated: '09/01/2025',
      category: 'Intellectual Property'
    }
  ];

  const categories = [...new Set(legalPages.map(page => page.category))];

  return (
    <div className="legal-notices-container">
      <div className="legal-notices-content">
        <header className="legal-notices-header">
          <div className="legal-notices-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" fill="#667eea"/>
              <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 13H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 9H8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="legal-notices-title">Legal Notices</h1>
          <p className="legal-notices-subtitle">
            Access all our legal documentation, policies, and compliance information
          </p>
          <div className="legal-notices-meta">
            <p><strong>Last updated:</strong> 09/01/2025</p>
            <p><strong>Company:</strong> SoundLegal.ai, Inc.</p>
          </div>
        </header>

        <div className="legal-notices-body">
          <section className="legal-notices-intro">
            <p>
              Welcome to our comprehensive legal documentation center. Here you'll find all the important legal notices, 
              policies, and agreements that govern your use of SoundLegal.ai services. We're committed to transparency 
              and making our legal information easily accessible and understandable.
            </p>
          </section>

          {categories.map(category => (
            <section key={category} className="legal-category">
              <h2 className="category-title">{category}</h2>
              <div className="legal-cards-grid">
                {legalPages
                  .filter(page => page.category === category)
                  .map(page => (
                    <Link
                      key={page.id}
                      to={page.url}
                      className="legal-card"
                      style={{ '--card-color': page.color } as React.CSSProperties}
                    >
                      <div className="legal-card-header">
                        <div className="legal-card-icon" style={{ backgroundColor: page.color }}>
                          {page.icon}
                        </div>
                        <div className="legal-card-meta">
                          <span className="last-updated">Updated {page.lastUpdated}</span>
                        </div>
                      </div>
                      <div className="legal-card-content">
                        <h3 className="legal-card-title">{page.title}</h3>
                        <p className="legal-card-description">{page.description}</p>
                      </div>
                      <div className="legal-card-footer">
                        <span className="view-document">
                          View Document →
                        </span>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          ))}

          <section className="legal-notices-info">
            <h2>Important Information</h2>
            <div className="info-grid">
              <div className="info-card">
                <h3>📅 Regular Updates</h3>
                <p>Our legal documents are regularly reviewed and updated to reflect changes in our services, applicable laws, and best practices.</p>
              </div>
              <div className="info-card">
                <h3>📧 Notifications</h3>
                <p>We'll notify you of any material changes to these documents via email or through our service interface.</p>
              </div>
              <div className="info-card">
                <h3>❓ Questions?</h3>
                <p>If you have questions about any of our legal documents, please contact us at <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
              </div>
              <div className="info-card">
                <h3>⚖️ Legal Compliance</h3>
                <p>These documents help ensure compliance with applicable laws including GDPR, CCPA, and other data protection regulations.</p>
              </div>
            </div>
          </section>

          <hr className="legal-divider" />

          <section className="legal-notices-contact">
            <h2>Contact & Support</h2>
            <div className="contact-grid">
              <div className="contact-item">
                <h3>General Legal Inquiries</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>Response Time:</strong> Within 24-48 hours</p>
              </div>
              <div className="contact-item">
                <h3>Data Protection Requests</h3>
                <p><strong>Email:</strong> <a href="mailto:info@soundlegal.ai">info@soundlegal.ai</a></p>
                <p><strong>For:</strong> GDPR/CCPA requests and privacy concerns</p>
              </div>
              <div className="contact-item">
                <h3>Copyright & DMCA</h3>
                <p><strong>URL:</strong> <a href="/dmca">https://soundlegal.ai/dmca</a></p>
                <p><strong>For:</strong> Copyright infringement reports</p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default LegalNotices;
